import type { ComponentType, Ref, SVGAttributes } from "react";

import { Box, Column, Tooltip } from "@hightouch/htui";
import { keyframes } from "@emotion/react";
import { useReducedMotion } from "motion/react";

import {
  ACTIVE_MARKER_RIGHT_PX,
  ACTIVE_MARKER_SIZE_PX,
  ACTIVE_MARKER_TOP_PX,
} from "src/components/active-marker-icon";
import { SidebarRightClosedIcon } from "src/ui/icons";
import { COLLAPSED_RAIL_BUTTON_BOX_SIZE } from "src/components/layout/page-sidebar/agent/constants";

// htui's text shimmer (`Text` with `shimmer`), the same sweep and timing, moved onto a mask.
const SHIMMER_SWEEP = keyframes`
  from { mask-position: 100% center; -webkit-mask-position: 100% center; }
  to { mask-position: -100% center; -webkit-mask-position: -100% center; }
`;
const SHIMMER_BAND =
  "linear-gradient(90deg, transparent 40%, black 50%, transparent 60%)";

// The panel icon gives way to the expand glyph on the button's hover or keyboard focus.
const HIDDEN_ON_REVEAL = {
  _groupHover: { opacity: 0, visibility: "hidden" },
  _groupFocusVisible: { opacity: 0, visibility: "hidden" },
} as const;

type CollapsedRailButtonProps = {
  icon: ComponentType<SVGAttributes<SVGElement>>;
  /** Tooltip message; may carry more context than the aria-label. */
  label: string;
  "aria-label": string;
  keyboardShortcut?: string;
  isExpanded: boolean;
  isDisabled?: boolean;
  /** The agent is working: the icon dims and shimmers, as the chat list's working rows do. */
  isWorking?: boolean;
  onClick: () => void;
  buttonRef?: Ref<HTMLButtonElement>;
  disableTooltip?: boolean;
};

/** Cross-fades the panel icon to an expand glyph on hover or keyboard focus. */
export function CollapsedRailButton({
  icon: Icon,
  label,
  "aria-label": ariaLabel,
  keyboardShortcut,
  isExpanded,
  isDisabled = false,
  isWorking = false,
  onClick,
  buttonRef,
  disableTooltip = false,
}: CollapsedRailButtonProps) {
  const shouldReduceMotion = useReducedMotion() ?? false;
  return (
    <Tooltip
      message={label}
      keyboardShortcut={keyboardShortcut}
      placement="left"
      isDisabled={disableTooltip}
    >
      <Column
        as="button"
        type="button"
        ref={buttonRef}
        aria-label={ariaLabel}
        aria-expanded={isExpanded}
        disabled={isDisabled}
        className="group"
        boxSize={COLLAPSED_RAIL_BUTTON_BOX_SIZE}
        borderRadius="md"
        align="center"
        justify="center"
        position="relative"
        color={isDisabled ? "text.disabled" : "text.secondary"}
        cursor={isDisabled ? "not-allowed" : "pointer"}
        transitionProperty="background-color, color"
        transitionDuration="fast"
        transitionTimingFunction="easeOutQuad"
        _hover={isDisabled ? undefined : { bg: "base.backgroundHover" }}
        _focusVisible={{ outline: "none", boxShadow: "outline" }}
        onClick={isDisabled ? undefined : onClick}
      >
        <Box
          as={Icon}
          position="absolute"
          boxSize={5}
          flexShrink={0}
          color={isWorking ? "text.tertiary" : undefined}
          transitionProperty="opacity"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          {...(!isDisabled && HIDDEN_ON_REVEAL)}
        />
        {isWorking && !shouldReduceMotion && (
          <AgentWorkingShimmer
            icon={Icon}
            boxSize={5}
            hidesOnReveal={!isDisabled}
          />
        )}
        <Box
          as={SidebarRightClosedIcon}
          position="absolute"
          boxSize={5}
          flexShrink={0}
          showArrow
          opacity={0}
          visibility="hidden"
          transitionProperty="opacity"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          {...(!isDisabled && {
            _groupHover: { opacity: 1, visibility: "visible" },
            _groupFocusVisible: { opacity: 1, visibility: "visible" },
          })}
        />
      </Column>
    </Tooltip>
  );
}

/**
 * A lighter copy of the agent's icon, laid over it, that htui's text shimmer sweeps across while the agent works.
 * The caller dims the icon underneath to `text.tertiary`, the shimmering text's color.
 */
export function AgentWorkingShimmer({
  icon,
  boxSize,
  hidesOnReveal = false,
}: {
  icon: ComponentType<SVGAttributes<SVGElement>>;
  boxSize: number;
  /** Gives way, as the icon under it does, when the button's hover or focus reveals another glyph. */
  hidesOnReveal?: boolean;
}) {
  return (
    <Box
      aria-hidden
      position="absolute"
      inset={0}
      display="flex"
      alignItems="center"
      justifyContent="center"
      pointerEvents="none"
      transitionProperty="opacity"
      transitionDuration="fast"
      transitionTimingFunction="easeOutQuad"
      {...(hidesOnReveal && HIDDEN_ON_REVEAL)}
    >
      <Box
        as={icon}
        boxSize={boxSize}
        flexShrink={0}
        color="gray.400"
        // A mask, because an icon has no text for htui's clipped background to fill.
        sx={{
          maskImage: SHIMMER_BAND,
          WebkitMaskImage: SHIMMER_BAND,
          maskSize: "200% auto",
          WebkitMaskSize: "200% auto",
          animation: `${SHIMMER_SWEEP} 2000ms linear infinite`,
        }}
      />
    </Box>
  );
}

/** The chat list's "Waiting for your reply" ring, at the unread dot's spot, for a collapsed panel whose open chat waits on the person. */
export function AgentAwaitingReplyMarker() {
  return (
    <Box
      aria-hidden
      pos="absolute"
      top={`${ACTIVE_MARKER_TOP_PX}px`}
      right={`${ACTIVE_MARKER_RIGHT_PX}px`}
      boxSize={`${ACTIVE_MARKER_SIZE_PX}px`}
      borderRadius="full"
      // Hollow like the chat list's, so it differs from the solid unread dot by shape as well as color.
      borderWidth="2px"
      borderStyle="solid"
      borderColor="warning.base"
      pointerEvents="none"
    />
  );
}
